/* =====================================================================
 * Tierfutterpro Predictive Prefetch
 * Inspired by Shopify Hydrogen's intent + viewport prefetch.
 *
 * Was es macht:
 *   1) Intent-Prefetch  -> mouseenter / focus / touchstart auf einem
 *                          Link triggert <link rel="prefetch"> der Ziel-URL.
 *   2) Viewport-Prefetch -> Links im Viewport werden langsam (low priority)
 *                           im Hintergrund prefetched.
 *   3) Image-Upgrade   -> Produkt-Bilder die im / nahe am Viewport sind,
 *                         werden von loading="lazy" auf "eager" hochgestuft,
 *                         damit sie sofort beim Scrollen verfügbar sind.
 *
 * Schutz:
 *   - Save-Data oder 2G/Slow-2G connection -> komplett aus.
 *   - Bereits prefetched -> nicht doppelt.
 *   - Externe Links / mailto / tel / # -> ignoriert.
 *   - data-no-prefetch Attribut -> ignoriert.
 *   - Max gleichzeitige Viewport-Prefetches limitiert.
 * ===================================================================== */

(function () {
  if (window.__tfpPrefetchInit) return;
  window.__tfpPrefetchInit = true;

  // ---- Connection / Daten-Sparen ----
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (conn && (conn.saveData || /^(2g|slow-2g|3g)$/.test(conn.effectiveType || ''))) {
    return; // Kein Prefetch auf langsamen Verbindungen (3G zaehlt jetzt auch).
  }

  // ---- Device-Erkennung: Mobile / Touch-only -> nur Intent-Prefetch ----
  // Mobile-Netze sind langsamer, RAM ist knapper, Background-Loads bremsen die
  // aktuelle Seite. Daher dort: KEIN Viewport-Prefetch, KEIN Prerender.
  var IS_MOBILE = window.matchMedia('(hover: none) and (pointer: coarse)').matches
               || window.innerWidth < 768;

  var ORIGIN = location.origin;
  var prefetched = new Set();        // URLs die schon prefetched wurden
  var viewportQueue = [];            // ausstehende viewport-Prefetches
  var inFlight = 0;
  var MAX_INFLIGHT = IS_MOBILE ? 1 : 4;   // Mobile: nur 1 parallel falls doch was triggert

  // ---- Helpers ----
  function isPrefetchable(href) {
    if (!href) return false;
    if (href.startsWith('#')) return false;
    if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return false;
    try {
      var url = new URL(href, location.href);
      if (url.origin !== ORIGIN) return false;                  // nur eigener Shop
      if (url.pathname === location.pathname && url.search === location.search) return false; // gleiche Seite
      return true;
    } catch (_) {
      return false;
    }
  }

  function addPrefetchLink(href, asType) {
    if (prefetched.has(href)) return;
    prefetched.add(href);
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    if (asType) link.as = asType;
    link.fetchPriority = asType === 'image' ? 'low' : 'auto';
    document.head.appendChild(link);
  }

  function prefetchHTML(href) {
    if (!isPrefetchable(href)) return;
    addPrefetchLink(href, 'document');
  }

  // ---- Speculation Rules API: prerender bei starker Intention ----
  // Browser baut die Ziel-Seite komplett im Hintergrund auf -> Klick = 0 ms.
  // Nur in Chrome 109+ / Edge 109+ unterstuetzt; andere Browser ignorieren.
  var prerendered = new Set();
  var MAX_PRERENDER = 3;     // Speicher- und Bandbreiten-Schutz
  function supportsSpeculationRules() {
    return !!(HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules'));
  }
  var canPrerender = supportsSpeculationRules() && !IS_MOBILE;   // Prerender NUR auf Desktop
  function tryPrerender(href) {
    if (!canPrerender) return;
    if (prerendered.has(href)) return;
    if (prerendered.size >= MAX_PRERENDER) return;
    if (!isPrefetchable(href)) return;
    prerendered.add(href);
    var script = document.createElement('script');
    script.type = 'speculationrules';
    script.textContent = JSON.stringify({
      prerender: [{ source: 'list', urls: [href], eagerness: 'moderate' }]
    });
    document.head.appendChild(script);
  }

  // ---- Intent (Hover / Focus / Touch) ----
  // Hover-Delay: 80 ms bevor wir prerendern, damit reine Mouse-Move-Events keinen Spam ausloesen.
  var hoverTimer = null;
  function onIntent(e) {
    var a = e.target.closest('a[href]');
    if (!a) return;
    if (a.hasAttribute('data-no-prefetch')) return;
    var href = a.getAttribute('href');
    var absUrl = a.href || href;
    prefetchHTML(absUrl);
    // Prerender mit kleinem Delay (filtert Drueberhuschen-Maus)
    if (hoverTimer) clearTimeout(hoverTimer);
    hoverTimer = setTimeout(function () { tryPrerender(absUrl); }, 80);
  }
  document.addEventListener('mouseenter', onIntent, true);
  document.addEventListener('focusin', onIntent, true);
  document.addEventListener('touchstart', onIntent, { capture: true, passive: true });

  // ---- Viewport-Prefetch fuer Links (idle, low priority) ----
  function processViewportQueue() {
    if (inFlight >= MAX_INFLIGHT) return;
    while (inFlight < MAX_INFLIGHT && viewportQueue.length) {
      var href = viewportQueue.shift();
      if (prefetched.has(href)) continue;
      inFlight++;
      addPrefetchLink(href, 'document');
      // Reset counter im naechsten Tick (sehr leichtgewichtig)
      setTimeout(function () { inFlight = Math.max(0, inFlight - 1); processViewportQueue(); }, 200);
    }
  }

  if ('IntersectionObserver' in window) {
    // Auf Mobile KEIN Viewport-Prefetch (nur Intent). Dummy-Observer der nichts tut.
    var linkObserver = IS_MOBILE ? null : new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var a = entry.target;
        linkObserver.unobserve(a);
        if (a.hasAttribute('data-no-prefetch')) return;
        if (!isPrefetchable(a.href)) return;
        viewportQueue.push(a.href);
      });
      if (window.requestIdleCallback) {
        requestIdleCallback(processViewportQueue, { timeout: 1500 });
      } else {
        setTimeout(processViewportQueue, 400);
      }
    }, { rootMargin: '300px 0px 300px 0px', threshold: 0.01 });

    // Bild-Upgrade-Observer: lazy -> eager wenn fast im Viewport
    var imgObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var img = entry.target;
        imgObserver.unobserve(img);
        if (img.loading === 'lazy') {
          // decoding hint + eager swap
          try { img.decoding = 'async'; } catch (_) {}
          img.loading = 'eager';
          // re-trigger src if needed
          if (img.dataset && img.dataset.src && !img.src) img.src = img.dataset.src;
        }
      });
    }, { rootMargin: '400px 0px 400px 0px', threshold: 0.01 });

    function observeAll(root) {
      if (linkObserver) {
        (root || document).querySelectorAll('a[href]:not([data-tfp-prefetch-bound])').forEach(function (a) {
          a.setAttribute('data-tfp-prefetch-bound', '1');
          linkObserver.observe(a);
        });
      }
      (root || document).querySelectorAll('img[loading="lazy"]:not([data-tfp-prefetch-bound])').forEach(function (img) {
        img.setAttribute('data-tfp-prefetch-bound', '1');
        imgObserver.observe(img);
      });
    }

    // Initial
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { observeAll(); });
    } else {
      observeAll();
    }

    // Spaeter via Theme-Editor oder Ajax nachgeladene Inhalte (z. B. Cart-Drawer, Filter)
    document.addEventListener('shopify:section:load', function (e) { observeAll(e.target); });
    var mo = new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        m.addedNodes.forEach(function (n) {
          if (n.nodeType === 1) observeAll(n);
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();
